import { useState, useEffect, useCallback } from 'react';
import { isNetworkError } from '@/api/client';

export function formatLoadError(err: unknown, offline: boolean): string {
  if (offline || isNetworkError(err)) {
    return 'Could not load data — check your connection and try again.';
  }
  return (err as { message?: string }).message ?? 'Failed to load';
}

export function useAsync<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setOffline(false);
    try {
      const result = await fetcher();
      setData(result);
    } catch (err) {
      const network = isNetworkError(err) || !navigator.onLine;
      setOffline(network);
      setError(formatLoadError(err, network));
      // Clear data on failed loads so empty tables/lists cannot masquerade as valid.
      setData(null);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    reload();
  }, [reload]);

  // Retry automatically when connectivity returns after a failed load.
  useEffect(() => {
    if (!offline) return;
    const onOnline = () => {
      void reload();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [offline, reload]);

  return { data, loading, error, offline, reload };
}

export function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return online;
}

export function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}
